import { useCallback, useMemo, useState } from "react";
import moment from "moment/min/moment-with-locales";
import { axiosInstance } from "../../auth/Auth";
import { useResolvedModules, useResolvedRadioModuleSettings } from "../HelperHooks";

export function useClocksData() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);

    const getData = useCallback(async (radioId, date, hour, cancelController) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`/clocks/${radioId}/${moment(date).format("YYYY-MM-DD")}/${hour}`, {
                signal: cancelController.signal,
            });
            setData(response.data);
            setErrorCode(null);
            setLoading(false);
        } catch (error) {
            if (error?.code !== "ERR_CANCELED") setErrorCode(error?.response?.status ?? 500);
        }
    }, []);

    return { loading, data, errorCode, getData };
}

export function useClocksRatingsData() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);

    const resolvedModules = useResolvedModules();
    const ratingsEnabled = useMemo(() => resolvedModules?.includes(2), [resolvedModules]);

    const getData = useCallback(
        async (radioId, demos, date, hour, cancelController) => {
            if (!ratingsEnabled) return;

            setLoading(true);
            try {
                if (!demos.length) {
                    setData(null);
                    return;
                }
                const startDatetime = moment(date).hours(hour).startOf("hour").format("YYYY-MM-DDTHH:mm:ss");
                const endDatetime = moment(date).hours(hour).endOf("hour").format("YYYY-MM-DDTHH:mm:ss");
                const radios = demos.map(x => {
                    return { radio_id: radioId, demo_id: x.demo_id };
                });
                const input = {
                    radios: radios,
                    start_datetime: startDatetime,
                    end_datetime: endDatetime,
                };
                const response = await axiosInstance.post(`/ratings/new/`, input, { signal: cancelController.signal });
                setData(response.data);
                setErrorCode(null);
            } catch (error) {
                setData(null);
                if (error?.code !== "ERR_CANCELED") setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        },
        [ratingsEnabled],
    );

    return { loading, data, errorCode, getData };
}

export function useClocksSessionsData() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);

    const resolvedModules = useResolvedModules();
    const ratingsEnabled = useMemo(() => resolvedModules?.includes(2), [resolvedModules]);

    const getData = useCallback(
        async (radioId, demoId, date, hour, average, cancelController) => {
            if (!ratingsEnabled) return;

            if (!demoId) {
                setData(null);
                return;
            }

            setLoading(true);
            try {
                const start_datetime = moment(date).startOf("day").format("YYYY-MM-DDTHH:mm:ss");
                const end_datetime = moment(date).endOf("day").format("YYYY-MM-DDTHH:mm:ss");
                const days = moment(date).isoWeekday() - 1;
                const h = Number(hour);

                const input = {
                    radios: [{ radio_id: radioId, demo_id: demoId }],
                    start_datetime,
                    end_datetime,
                    start_hour: hour,
                    end_hour: h + 1,
                    days: [days],
                    include_average: true,
                    last_year_as_averages: false,
                    grouping: 1,
                    exclude_mode: "both",
                };
                const response = await axiosInstance.post(`/tsl/session/`, input, { signal: cancelController.signal });
                setData(response.data);
                setErrorCode(null);
            } catch (error) {
                setData(null);
                if (error?.code !== "ERR_CANCELED") setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        },
        [ratingsEnabled],
    );

    return { loading, data, errorCode, getData };
}

export function useClocksContentBalanceData() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);

    const resolvedModules = useResolvedModules();
    const musicEnabled = useMemo(() => resolvedModules?.includes(5), [resolvedModules]);

    const getData = useCallback(
        async (radioId, date, hour, average, cancelController) => {
            if (!musicEnabled) return;

            setLoading(true);
            try {
                const startDatetime = moment(date).hours(hour).startOf("hour").format("YYYY-MM-DDTHH:mm:ss");
                const endDatetime = moment(date).hours(hour).endOf("hour").format("YYYY-MM-DDTHH:mm:ss");
                const startTime = moment(date).hours(hour).startOf("hour").format("HH:mm:ss");
                const endTime = moment(date).hours(hour).endOf("hour").format("HH:mm:ss");
                const days = moment(date).isoWeekday() - 1;
                const input = {
                    radio_id: radioId,
                    start_datetime: startDatetime,
                    end_datetime: endDatetime,
                    start_time: startTime,
                    end_time: endTime,
                    days: [days],
                    average: average,
                };
                const response = await axiosInstance.post(`/content_balance/`, input, { signal: cancelController.signal });
                setData(response.data);
                setErrorCode(null);
            } catch (error) {
                setData(null);
                if (error?.code !== "ERR_CANCELED") setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        },
        [musicEnabled],
    );

    return { loading, data, errorCode, getData };
}

export function useClocksContentData() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorCode, setErrorCode] = useState(null);

    const resolvedModules = useResolvedModules();
    const resolvedRadioModuleSettings = useResolvedRadioModuleSettings();

    const contentTestingEnabled = useMemo(() => resolvedModules?.includes(3), [resolvedModules]);

    const getData = useCallback(
        async (radioId, date, hour, cancelController) => {
            if (!contentTestingEnabled) return;
            if (!resolvedRadioModuleSettings.find(x => x.radio_id === radioId && x.module_id === 12 && x.data_type === 1)?.enabled) return;
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/clocks/${radioId}/${moment(date).format("YYYY-MM-DD")}/${hour}/content/`, {
                    signal: cancelController.signal,
                });
                setData(response.data);
                setErrorCode(null);
                setLoading(false);
            } catch (error) {
                setData(null);
                if (error?.code !== "ERR_CANCELED") setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        },
        [contentTestingEnabled, resolvedRadioModuleSettings],
    );

    return { loading, data, errorCode, getData, setData };
}
